import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

export const useGetReceiverChannels = (receiverId, options = {}) => {
  const { showToast } = useCustomToast();
  return useQuery(
    ['receiverChannels', receiverId],
    async () => (await axiosInstance.get(`/receivers/${receiverId}/channels`)).data,
    {
      enabled: !!receiverId && options.enabled !== false,
      onError: (err) => showToast('error', 'Channels', err.response?.data?.message),
    }
  );
};

export const useCreateChannel = (receiverId) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (payload) => axiosInstance.post(`/receivers/${receiverId}/channels`, payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(['receiverChannels', receiverId]);
        qc.invalidateQueries('receiversList');
        showToast('success', 'Channels', 'Channel added!');
      },
      onError: (err) => showToast('error', 'Channels', err.response?.data?.message),
    }
  );
};

export const useUpdateChannel = (receiverId) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, payload }) => axiosInstance.put(`/channels/${id}/update`, payload),
    {
      onSuccess: () => {
        if (receiverId) qc.invalidateQueries(['receiverChannels', receiverId]);
        showToast('success', 'Channels', 'Channel updated!');
      },
      onError: (err) => showToast('error', 'Channels', err.response?.data?.message),
    }
  );
};

export const useDeleteChannel = (receiverId) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (id) => axiosInstance.delete(`/channels/${id}/delete`),
    {
      onSuccess: () => {
        if (receiverId) qc.invalidateQueries(['receiverChannels', receiverId]);
        showToast('success', 'Channels', 'Channel removed!');
      },
      onError: (err) => showToast('error', 'Channels', err.response?.data?.message),
    }
  );
};

export const useSwitchChannel = () => {
  const { showToast } = useCustomToast();
  const qc = useQueryClient();
  return useMutation(
    ({ id, screen_ids }) => axiosInstance.post(`/channels/${id}/switch`, { screen_ids }),
    {
      onSuccess: () => {
        showToast('success', 'Channels', 'Channel switched!');
        // Refresh everything the switch just changed so the UI updates without
        // a manual page refresh:
        //  - receiversList: persisted active_channel_id → active-channel border
        //  - hardwareSources: player is now on HDMI_IN + previous_campaign_id
        //    set → the "Back to …" restore button appears
        //  - matrixRoutingState: encoder→screen colours on the screen cards
        qc.invalidateQueries('receiversList');
        qc.invalidateQueries('hardwareSources');
        qc.invalidateQueries('matrixRoutingState');
      },
      onError: (err) => showToast('error', 'Channels', err.response?.data?.message),
    }
  );
};

// Player-as-bridge IR commands. Both calls are fire-and-forget from the
// backend's perspective: it pushes a Firebase action to the receiver's
// player and returns immediately. The actual capture / blast happens on
// the venue LAN.
//
// For learn-ir, the captured code is POSTed back to the backend via the
// device-side /hardware/ir-learn-result endpoint, which updates the
// channel row. Callers should poll receiverChannels every 2s while the
// "Waiting…" state is up so the new ir_command appears without a manual
// refresh.
export const useLearnIRChannel = (receiverId) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, timeout = 10 }) =>
      axiosInstance.post(`/channels/${id}/learn-ir`, { timeout }),
    {
      onSuccess: () => {
        showToast(
          'success',
          'Learn IR',
          'Broadlink is listening. Press the channel button on your remote now.'
        );
        if (receiverId) qc.invalidateQueries(['receiverChannels', receiverId]);
      },
      onError: (err) => showToast('error', 'Learn IR', err.response?.data?.message),
    }
  );
};

export const useTestIRChannel = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id }) => axiosInstance.post(`/channels/${id}/test-ir`),
    {
      onSuccess: () => showToast('success', 'Test IR', 'IR code blasted via the player.'),
      onError: (err) => showToast('error', 'Test IR', err.response?.data?.message),
    }
  );
};
